import classnames from "classnames";
import { ComponentProps } from "react";
import { useIntl } from "react-intl";

import styles from "@components/v2/graphic_elements/Badge.module.css";
import IconBase from "@components/v2/icons/IconBase";
import { Colors } from "@utils/colorUtils";

type IconProps = Pick<ComponentProps<typeof IconBase>, "icon">;

type Props = {
  ariaLabel?: string;
  close?: () => void;
  color?: Extract<Colors, "gray-50" | "white" | "primary-300" | "pink" | "teal-50" | "primary-50">;
  icon?: IconProps["icon"];
  iconAlignRight?: boolean; // Icon will not display if `close` exists and `iconAlignRight` is true
  label: string;
  onClick?: () => void;
};

const Badge = ({ ariaLabel, close, color = "gray-50", icon, iconAlignRight = false, label, onClick }: Props) => {
  const intl = useIntl();

  const showIcon = (icon: IconProps["icon"]) => {
    if (iconAlignRight && close) {
      return;
    } else {
      return <IconBase className={styles.icon} icon={icon} />;
    }
  };

  return (
    <div
      className={classnames(
        "component-Badge",
        styles.badge,
        close && styles.hasX,
        iconAlignRight && styles.iconOnRight,
        styles[`color-${color}`]
      )}
    >
      {onClick ? (
        // eslint-disable-next-line react/forbid-elements
        <button aria-label={ariaLabel || label} className={styles.button} onClick={onClick}>
          {icon && showIcon(icon)}
          {label}
        </button>
      ) : (
        <div className={styles.button}>
          {icon && showIcon(icon)}
          {label}
        </div>
      )}

      {close && (
        // eslint-disable-next-line react/forbid-elements
        <button
          aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.close" })}
          className={styles.xButton}
          onClick={close}
        >
          <IconBase className={classnames(styles.xButtonIcon)} icon="xmark" />
        </button>
      )}
    </div>
  );
};

export default Badge;
